import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Layout } from "@/components/Layout";
import { findBlogPost, BLOG_POSTS, type BlogPost } from "@/lib/blog-posts";
import { getCustomBlogPost } from "@/lib/blog/admin.functions";
import { RichBlogRenderer, buildRichBlogSchema } from "@/components/blog/RichBlogRenderer";
import type { RichBlogPost } from "@/lib/blog/rich-types";

type LoaderData =
  | { kind: "legacy"; post: BlogPost }
  | { kind: "rich"; post: RichBlogPost };

export const Route = createFileRoute("/blog/$slug")({
  loader: async ({ params }): Promise<LoaderData> => {
    const legacy = findBlogPost(params.slug);
    if (legacy) return { kind: "legacy", post: legacy };
    try {
      const res = await getCustomBlogPost({ data: { slug: params.slug } });
      if (res.post) return { kind: "rich", post: res.post };
    } catch {
      /* fall through */
    }
    throw notFound();
  },
  head: ({ params, loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Post not found" }] };
    if (loaderData.kind === "rich") {
      const post = loaderData.post;
      const url = `https://drhint.com/blog/${post.slug}`;
      return {
        meta: [
          { title: (post.metaTitle ?? post.title).slice(0, 70) },
          { name: "description", content: post.description },
          { name: "keywords", content: post.keywords.join(", ") },
          { property: "og:title", content: post.title },
          { property: "og:description", content: post.description },
          { property: "og:type", content: "article" },
          { property: "og:url", content: url },
          { property: "article:published_time", content: post.date },
          ...(post.updated ? [{ property: "article:modified_time", content: post.updated }] : []),
        ],
        links: [{ rel: "canonical", href: url }],
        scripts: buildRichBlogSchema(post, url),
      };
    }
    const post = loaderData.post;
    return {
      meta: [
        { title: `${post.title} — DrHint` },
        { name: "description", content: post.description },
        { name: "keywords", content: post.keywords.join(", ") },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `/blog/${post.slug}` },
        { property: "article:published_time", content: post.date },
      ],
      links: [{ rel: "canonical", href: `/blog/${post.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.description,
            datePublished: post.date,
            author: { "@type": "Organization", name: "DrHint" },
          }),
        },
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Post not found</h1>
        <Link to="/blog" className="mt-3 inline-block text-primary underline">
          Back to blog
        </Link>
      </div>
    </Layout>
  ),
  errorComponent: ({ error }) => (
    <Layout>
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-2xl font-bold">Error loading post</h1>
        <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
      </div>
    </Layout>
  ),
});

function BlogPostPage() {
  const loaderData = Route.useLoaderData() as LoaderData;
  if (loaderData.kind === "rich") {
    return (
      <Layout>
        <RichBlogRenderer post={loaderData.post} />
      </Layout>
    );
  }
  const post = loaderData.post;
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <Layout>
      <article className="mx-auto max-w-3xl px-4 py-10">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/blog" className="hover:underline">Blog</Link>
        </nav>
        <div className="text-xs text-muted-foreground mb-2">
          {new Date(post.date).toLocaleDateString("en", { day: "numeric", month: "long", year: "numeric" })} ·{" "}
          {post.readMinutes} min read
        </div>
        <h1 className="text-3xl md:text-4xl font-bold leading-tight">{post.title}</h1>
        <p className="mt-3 text-lg text-muted-foreground">{post.description}</p>

        <div className="mt-8 space-y-6 leading-relaxed">
          {post.content.map((section, i) => (
            <section key={i}>
              {section.heading && <h2 className="text-xl font-bold mt-6 mb-2">{section.heading}</h2>}
              {section.paragraphs.map((p, j) => (
                <p key={j} className="mt-3 text-foreground/85">{p}</p>
              ))}
            </section>
          ))}
        </div>

        <div className="mt-12 rounded-xl border border-border bg-muted/40 p-5">
          <div className="font-semibold mb-1">Check today's live gold rates</div>
          <p className="text-sm text-muted-foreground mb-3">
            Now that you've read the guide, see what 24K and 22K gold costs in your city right now.
          </p>
          <div className="flex gap-2">
            <Link to="/pakistan" className="rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">
              Pakistan rates
            </Link>
            <Link to="/india" className="rounded-md border border-border bg-card px-3 py-2 text-xs font-semibold">
              India rates
            </Link>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-bold mb-3">More guides</h2>
          <ul className="space-y-2">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to="/blog/$slug" params={{ slug: r.slug }} className="text-primary underline">
                  {r.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </article>
    </Layout>
  );
}